import { useNavigate } from 'react-router';
import { Box, Center, Column, Row, Spinner, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { IPendingReview } from '~/domain/ShiftReview';
import { UserProfession } from '~/enums/UserProfession';
import { TableHeader } from './TableHeader';
import { useMediaQuery } from '~/hooks';
import { ExclamationCircleIcon } from '~/images/icons/ExclamationCircleIcon';
import { ClockEndIcon } from '~/images/icons/ClockEndIcon';
import { AdjustmentDetailV1 } from './AdjustmentDetailV1';
import { AdjustmentDetailV2 } from './AdjustmentDetailV2';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnforceOfficeDashboardV2 } from '~/constants/ff.posthog';
import { fireDashboardPendingReviewClicked } from '~/posthogs/events';

// Professional avatar border color matches the legacy neutral_100 divider.
const AVATAR_BORDER_COLOR = colors.neutral[100];

// Row bottom border — neutral_divider (#EDEDED) has no DS token; nearest is neutral[150] (#E7EAEB).
// Using the hex directly to preserve the exact legacy visual.
const ROW_DIVIDER_COLOR = '#EDEDED';

interface IProps {
  data: IPendingReview[];
  isLoading?: boolean;
}

export const Table = ({ data = [], isLoading = false }: IProps) => {
  const navigate = useNavigate();
  const [isSmallScreen] = useMediaQuery(['lg']);
  const useOfficeDashboardV2 = useFeatureFlag(EnforceOfficeDashboardV2);

  const handleRowClick = (item: IPendingReview) => {
    fireDashboardPendingReviewClicked();
    if (item.hasAdjustment) {
      navigate(`/dashboard/adjustments/${item?.id}`, { state: { job_id: item.id } });
      return;
    }
    navigate(`/dashboard/job/${item.id}/details`);
  };

  return (
    <table
      className="active-shifts-table"
      data-cy="pending-reviews-table"
      style={{ borderCollapse: 'collapse', width: '100%' }}
    >
      <TableHeader />
      <tbody>
        {isLoading && (
          <tr>
            <td colSpan={6}>
              <Center>
                <Spinner centered />
              </Center>
            </td>
          </tr>
        )}

        {!isLoading &&
          data.map((it) => (
            <tr
              key={it.id}
              data-cy={`pending-review-row-${it.id}`}
              style={{ borderBottom: `2px solid ${ROW_DIVIDER_COLOR}` }}
              onClick={() => handleRowClick(it)}
            >
              <td style={{ paddingTop: 14, paddingBottom: 8 }}>
                <img
                  src={it.professional.image}
                  style={{
                    border: `1px solid ${AVATAR_BORDER_COLOR}`,
                    borderRadius: 60,
                    height: 60,
                    objectFit: 'contain',
                    width: 60,
                  }}
                  alt="professional image"
                />
              </td>
              <td style={{ paddingTop: 14, paddingBottom: 8, paddingRight: 10 }}>
                <Column>
                  <Typography bold>{it.professional.fullName}</Typography>
                  <Typography color={colors.neutral[500]}>
                    {UserProfession.getName(it.profession)}
                  </Typography>
                </Column>
              </td>
              {!isSmallScreen && (
                <td style={{ paddingTop: 14, paddingBottom: 8, width: '30%' }}>
                  <Typography color={colors.neutral[500]} numberOfLines={2}>
                    {it.offerOwner.officeName}
                  </Typography>
                </td>
              )}
              <td style={{ paddingTop: 14, paddingBottom: 8, paddingRight: 10 }}>
                {useOfficeDashboardV2
                  ? <AdjustmentDetailV2 it={it} />
                  : <AdjustmentDetailV1 it={it} />}
              </td>
              <td
                data-cy={!it.hasAdjustment ? 'confirm-shift-action' : 'review-shift-action'}
                style={{ paddingTop: 14, paddingBottom: 8 }}
              >
                {!it.hasAdjustment ? (
                  <Row align="center" gap={1}>
                    <ClockEndIcon />
                    <Typography bold>Confirm</Typography>
                    <Box pl={3}>
                      <OutlineChevronRightIcon size={24} color={colors.secondary[500]} />
                    </Box>
                  </Row>
                ) : (
                  <Row align="center" gap={2}>
                    <ExclamationCircleIcon />
                    <Typography bold>Review</Typography>
                    <Box pl={3}>
                      <OutlineChevronRightIcon size={24} color={colors.secondary[500]} />
                    </Box>
                  </Row>
                )}
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  );
};
